import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

export const ForcePasswordChange = () => {
  const { changePassword, logout, formatApiErrorDetail } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated — welcome to ERIO.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0b0b0c]/95 p-4"
      data-testid="force-password-change"
    >
      <div className="w-full max-w-md rounded-md border border-white/10 bg-white p-8 shadow-2xl">
        <Logo />
        <div className="mt-6 flex items-center gap-2 text-ek-purple">
          <KeyRound size={18} />
          <p className="ek-eyebrow">First sign-in</p>
        </div>
        <h1 className="ek-heading mt-2 text-2xl">Set a new password</h1>
        <p className="mt-2 text-sm text-ek-ink/65">
          Your account was created with a temporary password. Choose a new one to continue.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            type="password"
            placeholder="Temporary password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            data-testid="fpc-current"
            className="w-full rounded-sm border border-black/15 px-3 py-2.5 text-sm focus:border-ek-purple focus:outline-none"
          />
          <input
            type="password"
            placeholder="New password (min 8 characters)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            data-testid="fpc-new"
            className="w-full rounded-sm border border-black/15 px-3 py-2.5 text-sm focus:border-ek-purple focus:outline-none"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            data-testid="fpc-confirm"
            className="w-full rounded-sm border border-black/15 px-3 py-2.5 text-sm focus:border-ek-purple focus:outline-none"
          />
          {error && (
            <p className="text-sm text-red-600" data-testid="fpc-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="fpc-submit"
            className="ek-btn w-full justify-center disabled:opacity-50"
          >
            {busy ? "Saving…" : "Update password"}
          </button>
        </form>
        <button
          onClick={logout}
          data-testid="fpc-logout"
          className="mt-4 w-full text-center text-xs text-ek-ink/65 hover:text-ek-ink"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
};
